"use client";

import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { createClient } from "@/utils/supabase/client";
import {
  SupabaseClient,
  RealtimeChannel,
  RealtimePostgresChangesPayload,
} from "@supabase/supabase-js";
import { Database } from "@/types/supabase";

type Callback = (
  payload: RealtimePostgresChangesPayload<{ [key: string]: any }>
) => void;

type ChannelRef = {
  channel: RealtimeChannel;
  callbacks: Set<Callback>;
};

type SupabaseContextType = {
  supabase: SupabaseClient<Database>;
  subscribe: (channelName: string, callback: Callback) => void;
  unsubscribe: (channelName: string, callback: Callback) => void;
};

const SupabaseContext = createContext<SupabaseContextType | undefined>(
  undefined
);

export function SupabaseProvider({ children }: { children: React.ReactNode }) {
  const supabase = useMemo(() => createClient(), []);
  const channelsRef = useRef<Map<string, ChannelRef>>(new Map());

  const subscribe = useCallback(
    (channelName: string, callback: Callback) => {
      let channelRef = channelsRef.current.get(channelName);
      if (!channelRef) {
        const newChannel = supabase.channel(channelName);
        channelRef = { channel: newChannel, callbacks: new Set() };
        channelsRef.current.set(channelName, channelRef);

        // Subscribe to all tables with a single listener
        newChannel
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "*" },
            (payload) => {
              // When a change occurs, notify all registered callbacks for this channel
              const callbacks = channelsRef.current.get(channelName)?.callbacks;
              callbacks?.forEach((cb) => cb(payload));
            }
          )
          .subscribe((status, err) => {
          if (err) {
            console.error(`Error subscribing to channel "${channelName}"`, err);
          }
        });
      }
      channelRef.callbacks.add(callback);
    },
    [supabase]
  );

  const unsubscribe = useCallback(
    (channelName: string, callback: Callback) => {
      const channelRef = channelsRef.current.get(channelName);
      if (channelRef) {
        channelRef.callbacks.delete(callback);
      }
    },
    [supabase]
  );

  // Clean up all channels when the provider unmounts
  useEffect(() => {
    return () => {
      channelsRef.current.forEach((channelRef) => {
        channelRef.callbacks.clear();
      });
    };
  }, [supabase]);

  const contextValue = useMemo(
    () => ({
      supabase,
      subscribe,
      unsubscribe,
    }),
    [supabase, subscribe, unsubscribe]
  );

  return (
    <SupabaseContext.Provider value={contextValue}>
      {children}
    </SupabaseContext.Provider>
  );
}

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error("useSupabase must be used within a SupabaseProvider");
  }
  return context;
};
